import { Spin } from 'antd'
import { getNoyoGroupPlan } from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.client'
import type { NoyoGroupPlan } from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.types'
import React, { useEffect, useState } from 'react'
import { PlanLabel } from './PlanLabel'

const planCache = new Map<string, Promise<NoyoGroupPlan>>()

export function fetchNoyoGroupPlan(planId: string): Promise<NoyoGroupPlan> {
  const cached = planCache.get(planId)
  if (cached) {
    return cached
  }
  const request = getNoyoGroupPlan(planId).catch((error) => {
    planCache.delete(planId)
    throw error
  })
  planCache.set(planId, request)
  return request
}

export function clearNoyoPlanCache() {
  planCache.clear()
}

type NoyoPlanCellProps = {
  planId?: string
  plan?: NoyoGroupPlan
  loading?: boolean
  highlightStart?: boolean
  highlightEnd?: boolean
}

export function NoyoPlanCell({
  planId,
  plan,
  loading,
  highlightStart,
  highlightEnd,
}: NoyoPlanCellProps) {
  const [fetchedPlan, setFetchedPlan] = useState<NoyoGroupPlan | null>(null)
  const [fetching, setFetching] = useState(false)

  useEffect(() => {
    if (plan != null) {
      setFetchedPlan(null)
      setFetching(false)
      return
    }
    if (typeof planId !== 'string' || planId.length === 0) {
      setFetchedPlan(null)
      setFetching(false)
      return
    }

    let cancelled = false
    setFetching(true)
    void fetchNoyoGroupPlan(planId)
      .then((next) => {
        if (cancelled) {
          return
        }
        setFetchedPlan(next)
      })
      .catch(() => {
        if (!cancelled) {
          setFetchedPlan(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFetching(false)
        }
      })

    return () => {
      cancelled = true
      setFetching(false)
    }
  }, [plan, planId])

  const resolved = plan ?? fetchedPlan
  if (resolved != null && resolved.name.length > 0) {
    return (
      <PlanLabel
        plan={resolved}
        highlightStart={highlightStart}
        highlightEnd={highlightEnd}
      />
    )
  }
  if (loading === true || fetching) {
    return <Spin size="small" />
  }
  return <span>—</span>
}
